import { Injectable } from '@angular/core';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
declare let window: any;

/**
 * Service for loading runtime environment configuration
 * Implements "Build Once, Deploy Anywhere" pattern
 * 
 * Configuration flow:
 * 1. Fetches environment name from /ui/host/environment endpoint (served by nginx)
 * 2. Loads environment-specific config from /assets/app-config/{envName}.json
 * 3. Falls back to app-config.json for local development
 * 4. Stores config in window.config for access via DynamicEnvironment class
 */
@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private defaultEnv = 'sit01-int'; // Default environment for local development

  /**
   * Initialize application configuration at bootstrap
   * Called by APP_INITIALIZER before application starts
   * @returns Promise that resolves when config is loaded
   */
  public async init(): Promise<any> {
    let envName = this.defaultEnv;
    let configPath = `${this.defaultEnv}.json`; // Fallback for local dev

    try {
      // Step 1: Try to fetch environment name from nginx endpoint
      // This endpoint only exists when deployed to PCF with nginx buildpack
      const envResponse = await fetch('/ui/host/environment');
      
      if (envResponse.ok) {
        const envText = await envResponse.text();
        const trimmedEnv = envText.trim();
        
        // Validate: environment name should be like "sit01-int" or "prod-ext"
        // If it looks like HTML (404 page), ignore it
        if (trimmedEnv && !trimmedEnv.startsWith('<') && trimmedEnv.includes('-')) {
          envName = trimmedEnv;
          
          // Config file path: Environment name directly maps to filename
          // Examples: "sit01-int" → sit01-int.json, "prod-ext" → prod-ext.json
          configPath = `${envName}.json`;
          
        } else {
          console.log('[AppInit] Invalid environment response, using local development config');
        }
      } else {
        console.log('[AppInit] Environment endpoint not available, using local development config');
      }
    } catch (error) {
      // This is expected during local development (ng serve)
      console.log('[AppInit] Running in local development mode');
    }

    // Step 2: Load environment-specific config file
    const configUrl = `assets/app-config/${configPath}`;
    const configResponse = await fetch(configUrl);
    
    if (!configResponse.ok) {
      throw new Error(`[AppInit] Failed to load config from ${configUrl} (HTTP ${configResponse.status})`);
    }
    
    const config = await configResponse.json();
    
    // Store config in window object for DynamicEnvironment access
    window.config = config;
    window.config.envName = envName;
    
    return config;
  }
}
